import { useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { QRCodeCanvas } from 'qrcode.react'
import {
	getCalendarConnections,
	updateCalendarPreferences,
	disconnectProvider,
	getIcalFeedUrl,
} from '../../features/calendarSlice'
import { toastHelper } from '../../util/toastHelper'
import { EVENT_CATEGORIES } from '../../util/constants'
import {
	CALENDAR_PROVIDERS,
	isProviderConnected,
} from '../../util/calendarProviders'
import ButtonCustom from '../atoms/button/ButtonCustom.jsx'
import SwitchBtn from '../atoms/switch-btn/SwicthBtn.jsx'
import styles from './settingsTabs.module.scss'

const REQUEST_URL = process.env.REACT_APP_REQUEST_URL || ''
const API_VERSION = 'v1'
const AUTH_BASE = `${REQUEST_URL}/api/${API_VERSION}/calendar`

export const CalendarIntegrations = () => {
	const dispatch = useDispatch()
	const { connections, preferences, loading } = useSelector(
		(state) => state.calendar
	)

	useEffect(() => {
		dispatch(getCalendarConnections())
	}, [dispatch])

	const handleConnect = useCallback(
		(providerId) => {
			if (providerId === 'ical') {
				dispatch(getIcalFeedUrl()).then((result) => {
					const path = result.payload?.feedPath
					if (result.payload?.success && path) {
						window.location.href = `${REQUEST_URL}${path}`
					} else {
						toastHelper('Could not get feed URL', 'error')
					}
				})
				return
			}
			window.location.href = `${AUTH_BASE}/auth/${providerId}`
		},
		[dispatch]
	)

	const handleDisconnect = useCallback(
		(provider) => {
			dispatch(disconnectProvider(provider)).then(() =>
				dispatch(getCalendarConnections())
			)
		},
		[dispatch]
	)

	const handleSyncEventsChange = useCallback(
		(provider, checked) => {
			dispatch(
				updateCalendarPreferences({ provider, syncEvents: checked })
			)
		},
		[dispatch]
	)

	const handleCategoryChange = useCallback(
		(provider, category, checked) => {
			const prefs = preferences[provider] || {}
			dispatch(
				updateCalendarPreferences({
					provider,
					categorySync: {
						...prefs.categorySync,
						[category]: checked,
					},
				})
			)
		},
		[dispatch, preferences]
	)

	const handleCopyIcalUrl = useCallback(async () => {
		let path = connections?.ical?.feedPath
		if (!path) {
			const result = await dispatch(getIcalFeedUrl()).unwrap()
			if (!result?.success || !result?.feedPath) {
				toastHelper('Could not get feed URL', 'error')
				return
			}
			path = result.feedPath
		}
		const url = `${REQUEST_URL}${path}`
		await navigator.clipboard.writeText(url)
		toastHelper('iCal feed URL copied to clipboard', 'success')
	}, [dispatch, connections?.ical?.feedPath])

	const isConnected = useCallback(
		(providerId) => isProviderConnected(connections, providerId),
		[connections]
	)

	const icalFeedUrlDisplay = useMemo(() => {
		if (!connections?.ical?.feedPath) return null
		return `${REQUEST_URL}${connections.ical.feedPath}`
	}, [connections?.ical?.feedPath])

	const icalFeedWebcalUrl = useMemo(() => {
		if (!icalFeedUrlDisplay) return null
		return icalFeedUrlDisplay.replace(/^https?:\/\//, 'webcal://')
	}, [icalFeedUrlDisplay])

	const isLoading = useMemo(() => {
		return (
			loading &&
			!connections?.google &&
			!connections?.outlook &&
			!(connections?.ical?.connected || connections?.ical?.feedPath)
		)
	}, [
		loading,
		connections?.google,
		connections?.outlook,
		connections?.ical?.connected,
		connections?.ical?.feedPath,
	])

	if (isLoading) {
		return (
			<div className={styles.profileCard}>
				<h1 className={styles.boxTitle}>Calendar Integrations</h1>
				<div className={styles.formBox}>
					<p>Loading calendar connections...</p>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.profileCard}>
			<h1 className={styles.boxTitle}>Calendar Integrations</h1>
			<div className={styles.formBox}>
				{CALENDAR_PROVIDERS.map(
					({ id: providerId, label, description, logo }) => {
						const connected = isConnected(providerId)
						return (
							<div
								key={providerId}
								className={styles.integrationCard}
								style={
									!connected ? { paddingBottom: '16px' } : {}
								}
							>
								<div className={styles.cardHeader}>
									<div className={styles.cardHeaderLeft}>
										<div className={styles.cardLogo}>
											<img src={logo} alt={label} />
										</div>
										<div>
											<h2 className={styles.cardTitle}>
												{label}
											</h2>
											<p
												className={
													styles.cardDescription
												}
											>
												{description}
											</p>
											<div
												className={
													styles.statusContainer
												}
											>
												Status:
												<div
													className={`${styles.statusBadge} ${connected ? styles.connected : styles.disconnected}`}
												>
													{connected && (
														<img
															src="/images/tick-circle.svg"
															alt="Connected"
															loading="lazy"
															className={
																styles.statusIcon
															}
														/>
													)}
													{connected
														? 'Connected'
														: 'Not connected'}
												</div>
											</div>
										</div>
									</div>
									<div className={styles.cardActions}>
										{connected ? (
											<ButtonCustom
												variant="outlined"
												onClick={() =>
													handleDisconnect(providerId)
												}
											>
												Disconnect
											</ButtonCustom>
										) : (
											<ButtonCustom
												variant="default"
												className={styles.connectBtn}
												onClick={() =>
													handleConnect(providerId)
												}
											>
												Connect
											</ButtonCustom>
										)}
									</div>
								</div>

								{connected && (
									<>
										<hr className={styles.sectionDivider} />
										<div
											className={
												styles.integrationCardConnectedContent
											}
										>
											{providerId === 'ical' &&
												connections?.ical?.feedPath && (
													<>
														<div
															className={
																styles.feedUrlBox
															}
														>
															<div
																className={
																	styles.feedUrlLayout
																}
															>
																{icalFeedWebcalUrl && (
																	<div
																		className={
																			styles.feedUrlColumn
																		}
																	>
																		<p
																			className={
																				styles.feedUrlLabel
																			}
																		>
																			Scan
																			to
																			subscribe
																		</p>
																		<QRCodeCanvas
																			value={
																				icalFeedWebcalUrl
																			}
																			aria-label="QR code for iCal feed subscription"
																			size={
																				160
																			}
																		/>
																	</div>
																)}

																<div
																	className={
																		styles.feedUrlOrColumn
																	}
																>
																	OR
																</div>

																<div
																	className={
																		styles.feedUrlColumn
																	}
																>
																	<p
																		className={
																			styles.feedUrlLabel
																		}
																	>
																		Use the
																		link
																	</p>
																	<div
																		className={
																			styles.feedUrlInputRow
																		}
																	>
																		{icalFeedUrlDisplay && (
																			<p
																				className={
																					styles.feedUrlValue
																				}
																				title={
																					icalFeedUrlDisplay
																				}
																			>
																				{
																					icalFeedUrlDisplay
																				}
																			</p>
																		)}
																		<ButtonCustom
																			variant="default"
																			onClick={
																				handleCopyIcalUrl
																			}
																		>
																			Copy
																		</ButtonCustom>
																	</div>
																</div>
															</div>
														</div>
														<hr
															className={
																styles.sectionDivider
															}
														/>
													</>
												)}

											<>
												<h3
													className={
														styles.sectionHeader
													}
												>
													Sync settings
												</h3>
												<div
													className={
														styles.settingsGroup
													}
												>
													<div
														className={
															styles.settingRow
														}
													>
														<span
															className={
																styles.settingRowLabel
															}
														>
															Sync tasks/events
														</span>
														<SwitchBtn
															checked={
																preferences[
																	providerId
																]
																	?.syncEvents !==
																false
															}
															setChecked={(val) =>
																handleSyncEventsChange(
																	providerId,
																	val
																)
															}
														/>
													</div>
												</div>

												<hr
													className={
														styles.sectionDivider
													}
												/>

												<h3
													className={
														styles.sectionHeader
													}
													style={{
														marginTop: '16px',
													}}
												>
													Category sync
												</h3>
												<div
													className={
														styles.settingsGroup
													}
												>
													{EVENT_CATEGORIES.map(
														(cat) => (
															<div
																key={cat}
																className={
																	styles.settingRow
																}
															>
																<span
																	className={
																		styles.settingRowLabel
																	}
																>
																	{cat}
																</span>
																<SwitchBtn
																	checked={
																		preferences[
																			providerId
																		]
																			?.categorySync?.[
																			cat
																		] !==
																		false
																	}
																	setChecked={(
																		val
																	) =>
																		handleCategoryChange(
																			providerId,
																			cat,
																			val
																		)
																	}
																/>
															</div>
														)
													)}
												</div>
											</>
										</div>
									</>
								)}
							</div>
						)
					}
				)}
			</div>
		</div>
	)
}
